import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogFooter } from 'components/ui/dialog';
import { useCallback, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { t } from 'utils/getTranslations';
import getCroppedImg from './getCroppedImg';

interface CropDialogProps {
    imageSrc: string;
    onClose: () => void;
    onConfirm: (croppedBlob: Blob) => void;
    aspect?: number;
    cropShape?: 'rect' | 'round';
}

const CropDialog = ({ imageSrc, onClose, onConfirm, aspect = 1, cropShape = 'round' }: CropDialogProps) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

    const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    const handleConfirm = async () => {
        if (!croppedAreaPixels) return;
        try {
            const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
            onConfirm(croppedBlob);
        } catch {
            onClose();
        }
    };

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="gap-0 p-0 sm:max-w-xl" showCloseButton={false}>
                <div className="relative h-100">
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspect}
                        cropShape={cropShape}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                    />
                </div>
                <DialogFooter className="flex-row items-center gap-4 px-6 py-4">
                    {/* The native range input is the zoom control: the kit has no slider, and a
                        bespoke one would buy nothing over what the platform already draws. */}
                    <input
                        type="range"
                        aria-label={t('image-input.zoom')}
                        className="accent-primary flex-1 cursor-pointer"
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.1}
                        onChange={(event) => setZoom(Number(event.target.value))}
                    />
                    <Button type="button" variant="ghost" onClick={onClose}>
                        {t('cancel')}
                    </Button>
                    <Button
                        type="button"
                        onClick={() => {
                            void handleConfirm();
                        }}
                    >
                        {t('save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default CropDialog;
